// Keyword-based matcher that picks the most relevant imported locator screen
// for a generated test case. Used to auto-fill the Mobile POM editor so the
// user does not have to type locators by hand.
import { MOBILE_LOCATOR_SCREENS } from '../data/locators';

// Extra hint words per screen (keyed by lowercased screen name) to improve
// matching beyond the tokens we can derive from the screen/locator names.
const SCREEN_SYNONYMS = {
  login: ['login', 'signin', 'log', 'credential', 'authenticate', 'auth', 'password'],
  register: ['register', 'signup', 'registration', 'createaccount', 'enroll', 'account'],
  // 'verify'/'verification' were removed: they are generic QA vocabulary that
  // appears in nearly every test-case title and used to pull unrelated cases
  // onto Onboarding. Keep only domain-specific onboarding/KYC hints.
  onboarding: ['onboarding', 'onboard', 'kyc', 'aadhaar', 'pan', 'document'],
  consents: ['consent', 'consents', 'agree', 'terms', 'permission'],
  contacts: ['contact', 'contacts', 'phone', 'email'],
  leads: ['lead', 'leads', 'dashboard', 'home'],
  leaddetail: ['lead', 'detail', 'details', 'profile'],
  addlead: ['add', 'create', 'lead'],
};

// Generic QA / product-ambient words that must never drive screen selection.
// Mirrors the backend web POM stopwords (see _POM_RECOMMENDATION_STOPWORDS in
// app/api/routes/playwright.py) so mobile matching behaves like the web ranker.
const MATCH_STOPWORDS = new Set([
  // filler
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'into', 'when', 'then',
  'will', 'shall', 'should', 'must', 'able', 'given', 'in', 'to', 'of', 'on',
  'at', 'is', 'it', 'be', 'as', 'an', 'or', 'by', 'no', 'do', 'we',
  // generic QA vocabulary
  'user', 'users', 'test', 'tests', 'testing', 'case', 'cases', 'scenario',
  'step', 'steps', 'verify', 'verifies', 'verified', 'validate', 'validates',
  'validation', 'check', 'checks', 'ensure', 'confirm', 'click', 'clicks',
  'tap', 'taps', 'enter', 'enters', 'input', 'inputs', 'select', 'selects',
  'field', 'fields', 'button', 'buttons', 'expected', 'result', 'results',
  'positive', 'negative', 'valid', 'invalid', 'successfully', 'success',
  'display', 'displays', 'displayed', 'show', 'shows', 'view', 'views',
  'open', 'opens', 'navigate', 'navigates', 'correct', 'correctly', 'data',
  'value', 'values',
  // product-ambient (this IS a mobile app; these words carry no screen signal)
  'app', 'application', 'mobile', 'system', 'screen', 'page',
]);

// Weight floor at which a matched keyword counts as a "strong" signal: a screen
// name (weight 5) or curated synonym (weight 4). Locator-derived tokens (weight
// 1-2) are "weak" and must not, on their own, outrank a real name/synonym match.
const STRONG_WEIGHT = 4;

// Split camelCase / snake_case / separators into lowercase word tokens.
export const tokenize = (str) =>
  String(str || '')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-./:]+/g, ' ')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1);

// Build a weighted keyword bag for one screen.
const buildScreenKeywords = (screen) => {
  const weights = new Map();
  const add = (token, w) => {
    if (!token || token.length < 2) return;
    weights.set(token, Math.max(weights.get(token) || 0, w));
  };
  // Screen name tokens are the strongest signal.
  tokenize(screen.screen).forEach((t) => add(t, 5));
  (SCREEN_SYNONYMS[screen.screen.toLowerCase()] || []).forEach((t) => add(t, 4));
  // Locator names + values give secondary signal.
  (screen.locators || []).forEach((loc) => {
    tokenize(loc.name).forEach((t) => add(t, 2));
    tokenize(loc.value).forEach((t) => add(t, 1));
  });
  return weights;
};

const SCREEN_KEYWORDS = MOBILE_LOCATOR_SCREENS.map((screen) => ({
  screen,
  keywords: buildScreenKeywords(screen),
}));

// Collect searchable text from a test case across its common fields.
export const getTestCaseText = (testCase) => {
  if (!testCase || typeof testCase !== 'object') return '';
  const parts = [];
  const push = (v) => {
    if (!v) return;
    if (Array.isArray(v)) v.forEach(push);
    else if (typeof v === 'object') Object.values(v).forEach(push);
    else parts.push(String(v));
  };
  [
    // `test_description` / `test_name` hold the real per-case text in this app's
    // schema — without them the matcher was blind to most cases and mis-matched
    // the screen (e.g. a Consents case not getting the Consents locators).
    'test_description', 'test_name',
    'scenario', 'title', 'test_case_title', 'description', 'summary',
    'objective', 'preconditions', 'steps', 'test_steps', 'expected_result',
    'expected', 'module', 'feature', 'category', 'tags',
  ].forEach((key) => push(testCase[key]));
  return parts.join(' ');
};

// Document frequency: how many screens share each token. Tokens common to many
// screens are poor discriminators — critically, in this lead-origination app the
// word "lead" is a screen-name token for AddLead/Leads/LeadDetail AND appears in
// nearly every test case, so without this it swamps the real signal (e.g. a
// "consent … for a lead" case matching AddLead instead of Consents). Scaling each
// token's weight by 1/df lets a unique word like "consent" or "login" win.
const SCREEN_DF = (() => {
  const df = new Map();
  SCREEN_KEYWORDS.forEach(({ keywords }) => {
    keywords.forEach((_weight, token) => df.set(token, (df.get(token) || 0) + 1));
  });
  return df;
})();

/**
 * Rank screens for a test case. Returns an array of
 * { screen, strong, weak, score, index } sorted best-first.
 *
 * Scoring separates a `strong` signal (screen-name / curated-synonym hits) from
 * a `weak` signal (incidental locator-value token overlaps), each scaled by the
 * token's discriminativeness (1/df). Ranking on strong first means a genuine,
 * distinctive name match beats a screen that merely shares a ubiquitous word;
 * ties fall back to catalog order — never to screen size.
 */
export const rankScreensForTestCase = (testCase) => {
  const caseTokens = new Set(
    tokenize(getTestCaseText(testCase)).filter((t) => !MATCH_STOPWORDS.has(t))
  );
  return SCREEN_KEYWORDS.map(({ screen, keywords }, index) => {
    let strong = 0;
    let weak = 0;
    keywords.forEach((weight, token) => {
      if (!caseTokens.has(token)) return;
      const effective = weight / (SCREEN_DF.get(token) || 1);
      if (weight >= STRONG_WEIGHT) strong += effective;
      else weak += effective;
    });
    return { screen, strong, weak, score: strong + weak, index };
  }).sort((a, b) => b.strong - a.strong || b.weak - a.weak || a.index - b.index);
};

/**
 * Best screen for a test case. Always returns a screen so the editor is never
 * empty. `confident` is true only when the match rests on a strong (name /
 * synonym) signal; a weak locator-only overlap still surfaces its best guess
 * but is flagged low-confidence so the UI prompts the user to confirm, and a
 * total miss (no overlap at all) falls back to `fallbackScreen`.
 */
export const matchScreenForTestCase = (testCase, { fallbackScreen = 'Leads' } = {}) => {
  const ranked = rankScreensForTestCase(testCase);
  const top = ranked[0];
  if (top && top.score > 0) {
    // Confident only on a strong signal AND when there is no near-tie runner-up
    // — an ambiguous case (e.g. "log in and view the dashboard": Login vs Leads)
    // should prompt the user to confirm rather than silently pick one.
    const runnerUp = ranked[1];
    const ambiguous = !!runnerUp
      && runnerUp.strong > 0
      && runnerUp.strong >= top.strong * 0.8;
    return { screen: top.screen, score: top.score, confident: top.strong > 0 && !ambiguous };
  }
  const fallback =
    MOBILE_LOCATOR_SCREENS.find((s) => s.screen.toLowerCase() === fallbackScreen.toLowerCase())
    || MOBILE_LOCATOR_SCREENS[0]
    || null;
  return { screen: fallback, score: 0, confident: false };
};

// Deep-copy a screen's locators so editor edits don't mutate the bundled data.
export const cloneScreenLocators = (screen) =>
  (screen?.locators || []).map((loc) => ({ ...loc }));
